<script lang="ts">
import { formatAbsence, type NxPrincipal, type NxPrincipalAbsence } from './principals';

/** „5 Mitarbeiter“ / „3 Mitglieder“ — the right-hand side of a department or group suggestion. */
function defaultCountLabel(principal: NxPrincipal): string {
  const count = principal.memberCount ?? 0;
  if (principal.kind === 'group') return count === 1 ? '1 Mitglied' : `${count} Mitglieder`;
  return `${count} Mitarbeiter`;
}

/** Accessible name of the „→ Vertretung“ button, including a substitute's own absence. */
function defaultSubstituteLabel(substitute: NxPrincipal): string {
  const away = substitute.absence ? ` (selbst abwesend: ${formatAbsence(substitute.absence)})` : '';
  return `An Vertretung ${substitute.label} senden${away}`;
}

function defaultAbsenceText(absence: NxPrincipalAbsence): string {
  return formatAbsence(absence);
}

function isThenable<T>(value: unknown): value is PromiseLike<T> {
  return typeof (value as PromiseLike<T> | null)?.then === 'function';
}
</script>

<script setup lang="ts">
/**
 * Picks the recipients of a message or an activity: persons, departments and
 * groups, as chips in one field with the text input inside. Built for the
 * activity dialog's „Empfänger (verantwortlich)“ and „Kenntnisnahme (Cc)“ —
 * one component, used twice (`variant="to"` | `"cc"`, Cc chips are grey) —
 * and the chat's quick form (`compact`).
 *
 * The kit knows no directory. `search(query, signal)` is the caller's lookup
 * and may answer synchronously or with a promise; it is asked `debounce` ms
 * after the last keystroke, and a newer query aborts the older one (its late
 * answer is dropped). The hits are grouped „Nutzer / Abteilungen / Gruppen“,
 * capped per kind (`limits`, default 4/3/3), and never offer what is already
 * chosen or listed in `excludeIds` (e.g. the signed-in user).
 *
 * - Chips: one colour per kind (`NX_PRINCIPAL_HUES`); an absent person gets
 *   the warning tint, a plane and the absence as tooltip. × or Backspace in the
 *   empty input removes; adding a principal twice is ignored.
 * - Absence: the `absence` slot below the field gets the absent persons (and
 *   `replaceWithSubstitute`) so the caller can compose its warning card. Without
 *   the slot, the „An“ field shows one line per absent person with „→ Vertretung“.
 * - Collective: whether a department or group is chosen is emitted as
 *   `collective-change` and exposed as `hasCollective` — in „An“ that is when the
 *   caller offers „Einer übernimmt / Jeder einzeln / Nur Kenntnis“.
 * - `required`: marked invalid when left empty (after the field is left, or on
 *   `validate()`), with the message below the field. `invalid` forces the look.
 * - `readonly`: the recipients are fixed („nur ich“) — no input, no ×.
 * - `compact`: small chips without field chrome, no input; „+“ emits `expand`
 *   so the caller opens the full dialog.
 *
 * Keyboard (combobox + listbox pattern, focus stays in the input): ↓/↑ move
 * through the suggestions (↓ also reopens them), Enter adds the highlighted one
 * (the first hit is highlighted when results arrive), Escape closes the list and
 * then clears the query, Backspace in the empty input removes the last chip.
 * Additions and removals are announced in a polite live region.
 */
import { computed, inject, nextTick, onBeforeUnmount, ref, shallowRef, useId, watch } from 'vue';
import { AddIcon, PeopleIcon } from '@blpde/nx-icons';
import Plane from 'lucide-vue-next/dist/esm/icons/plane.js';
import NxAvatar from '../Avatar/NxAvatar.vue';
import NxBadge from '../Badge/NxBadge.vue';
import NxButton from '../Button/NxButton.vue';
import NxChip from '../Chip/NxChip.vue';
import NxDropdown from '../Dropdown/NxDropdown.vue';
import NxIconButton from '../IconButton/NxIconButton.vue';
import NxTagField from '../Inputs/NxTagField.vue';
import { labelInjectionKey } from '../Inputs/internal';
import NxConditionalWrapper from '../lib/NxConditionalWrapper.vue';
import { hueVars } from '../lib/hue';
import NxTooltip from '../Tooltip/Tooltip.vue';
import {
  absentPrincipals,
  addPrincipal,
  DEFAULT_PRINCIPAL_KIND_LABELS,
  groupPrincipals,
  hasCollectivePrincipal,
  NX_PRINCIPAL_HUES,
  principalKey,
  principalLabel,
  principalShortLabel,
  substitutePrincipal,
  type NxPrincipalId,
  type NxPrincipalKind,
  type NxPrincipalKindLabels,
  type NxPrincipalLimits,
  type NxPrincipalRef,
  type NxPrincipalSearch,
} from './principals';

const props = withDefaults(
  defineProps<{
    /** The directory lookup. Without it the field only shows and removes. */
    search?: NxPrincipalSearch;
    /** `to` colours the chips by kind; `cc` („nur Kenntnis“) keeps them grey. */
    variant?: 'to' | 'cc';
    /** Quick-form variant: small chips, no input, „+“ emits `expand`. */
    compact?: boolean;
    /**
     * Never suggested. A plain id stands for a PERSON (typically the signed-in
     * user); a `{ kind, id }` — e.g. the other field's principals — excludes
     * exactly that one. What is already chosen is never suggested anyway.
     */
    excludeIds?: readonly (NxPrincipalId | NxPrincipalRef)[];
    /** Suggestions per kind; defaults to 4 persons, 3 departments, 3 groups. */
    limits?: NxPrincipalLimits;
    /** At least one recipient is needed. */
    required?: boolean;
    /** Force the invalid look (e.g. a rejection from the server). */
    invalid?: boolean;
    /** Fixed recipients: shown, not editable (no input, no ×). */
    readonly?: boolean;
    disabled?: boolean;
    /** Pause after the last keystroke before `search` is asked (ms); `0` asks at once. */
    debounce?: number;
    /** Characters needed before suggestions are searched; `0` also searches the empty query on ↓. */
    minQueryLength?: number;
    /** Id of the text input; generated (and tied to a surrounding label) when omitted. */
    id?: string;
    /** Accessible name when no visible label (`NxInputContainerItem`, `NxLabel`) names the field. */
    ariaLabel?: string;
    /** Placeholder of the empty field; defaults by variant. */
    placeholder?: string;
    /** Placeholder once recipients are chosen. */
    placeholderMore?: string;
    /** Shown instead of chips when nothing is chosen and there is no input (compact, readonly). */
    emptyText?: string;
    /** Headings of the suggestion groups. */
    categoryLabels?: Record<NxPrincipalKind, string>;
    /** Words in front of a department's / group's name on a chip. */
    kindLabels?: NxPrincipalKindLabels;
    /** Right-hand side of a department / group suggestion that carries a `memberCount`. */
    countLabel?: (principal: NxPrincipal) => string;
    noResultsLabel?: string;
    searchingLabel?: string;
    /** Accessible name of a chip's ×. */
    removeLabel?: (label: string) => string;
    /** Accessible name of the compact „+“. */
    expandLabel?: string;
    requiredMessage?: string;
    /** How an absence reads (chip tooltip, suggestion tag, notice). */
    absenceText?: (absence: NxPrincipalAbsence) => string;
    /** Accessible name of the „→ Vertretung“ button of the default notice. */
    substituteLabel?: (substitute: NxPrincipal) => string;
    /** Live-region announcement after a principal was added. */
    addedLabel?: (label: string) => string;
    /** Live-region announcement after a principal was removed. */
    removedLabel?: (label: string) => string;
  }>(),
  {
    search: undefined,
    variant: 'to',
    compact: false,
    excludeIds: () => [],
    limits: () => ({}),
    required: false,
    invalid: false,
    readonly: false,
    disabled: false,
    debounce: 150,
    minQueryLength: 1,
    id: undefined,
    ariaLabel: undefined,
    placeholder: undefined,
    placeholderMore: 'weitere …',
    emptyText: 'Kein Empfänger',
    categoryLabels: () => ({ person: 'Nutzer', department: 'Abteilungen', group: 'Gruppen' }),
    kindLabels: () => ({ ...DEFAULT_PRINCIPAL_KIND_LABELS }),
    countLabel: defaultCountLabel,
    noResultsLabel: 'Kein Treffer',
    searchingLabel: 'Suche …',
    removeLabel: (label: string) => `${label} entfernen`,
    expandLabel: 'Empfänger hinzufügen',
    requiredMessage: 'Bitte mindestens einen Empfänger angeben',
    absenceText: defaultAbsenceText,
    substituteLabel: defaultSubstituteLabel,
    addedLabel: (label: string) => `${label} hinzugefügt`,
    removedLabel: (label: string) => `${label} entfernt`,
  }
);

const model = defineModel<NxPrincipal[]>({ required: true });

const emit = defineEmits<{
  /** A principal was added (picked, or as a substitute). */
  add: [principal: NxPrincipal];
  /** A principal was removed (×, Backspace, or replaced by its substitute). */
  remove: [principal: NxPrincipal];
  /** A department or group entered the list, or the last one left it. */
  'collective-change': [hasCollective: boolean];
  /** Compact „+“: open the full picker. */
  expand: [];
}>();

const slots = defineSlots<{
  /** Below the field while absent persons are chosen — the caller's warning card. */
  absence?(props: {
    absent: NxPrincipal[];
    replaceWithSubstitute: (principal: NxPrincipal) => void;
  }): unknown;
}>();

const inputId = props.id ?? useId();
const listboxId = `${inputId}-listbox`;
const errorId = `${inputId}-error`;

// Inside NxInputContainerItem / NxLabel the caption names the text input.
const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) labelInjection.setFor(inputId);

const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const popupRef = ref<HTMLElement | null>(null);

const interactive = computed(() => !props.readonly && !props.disabled);
const hasInput = computed(() => !props.compact && !props.readonly);

/**
 * The full field is NxTagField's chrome, capped at three chip rows plus the
 * input (its 5.5em default hides the input line behind two rows); compact is a
 * plain row without chrome.
 */
const field = computed(() =>
  props.compact ? { is: 'div', attrs: {} } : { is: NxTagField, attrs: { maxHeight: '7.5em' } }
);

// ── Validation ──────────────────────────────────────────────────────────────
const touched = ref(false);
const missing = computed(() => props.required && model.value.length === 0);
const showRequiredError = computed(() => missing.value && (touched.value || props.invalid));
const isInvalid = computed(() => props.invalid || showRequiredError.value);

// ── Chips ───────────────────────────────────────────────────────────────────
function chipLabel(principal: NxPrincipal): string {
  return principalLabel(principal, { count: true, kindLabels: props.kindLabels });
}

function isAbsent(principal: NxPrincipal): boolean {
  return principal.kind === 'person' && !!principal.absence;
}

function absenceOf(principal: NxPrincipal): string {
  return principal.absence ? props.absenceText(principal.absence) : '';
}

/** Kind colour in „An“; Cc chips stay grey; absence overrides both with the warning tint. */
function chipHue(principal: NxPrincipal) {
  return props.variant === 'cc' || isAbsent(principal)
    ? undefined
    : NX_PRINCIPAL_HUES[principal.kind];
}

const absent = computed(() => absentPrincipals(model.value));
const hasCollective = computed(() => hasCollectivePrincipal(model.value));

watch(hasCollective, (value) => emit('collective-change', value));

// ── Live region ─────────────────────────────────────────────────────────────
const announcement = ref('');

function announce(message: string) {
  // Cleared first so the same sentence twice in a row is announced twice.
  announcement.value = '';
  void nextTick(() => {
    announcement.value = message;
  });
}

// ── Suggestions ─────────────────────────────────────────────────────────────
const query = ref('');
/** The user wants the list: set by typing / ↓, cleared by Escape, a pick, leaving. */
const open = ref(false);
/** A search for the current query is scheduled or on its way. */
const loading = ref(false);
/** Last delivered hits — while `loading`, those of the previous query. */
const results = shallowRef<readonly NxPrincipal[] | null>(null);
let resultsQuery = '';
const activeIndex = ref(-1);

let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchAbort: AbortController | null = null;
let searchSeq = 0;
/** Enter came before the hits: take the first one as soon as they arrive. */
let pickOnArrival = false;

const groups = computed(() =>
  results.value
    ? groupPrincipals(results.value, {
        exclude: [...model.value, ...props.excludeIds],
        limits: props.limits,
      })
    : []
);

/** Groups with each option's position in the flat keyboard order. */
const indexedGroups = computed(() => {
  let index = 0;
  return groups.value.map((group) => ({
    kind: group.kind,
    items: group.items.map((principal) => ({ principal, index: index++ })),
  }));
});

const options = computed(() => groups.value.flatMap((group) => group.items));

const popupVisible = computed(
  () =>
    hasInput.value && interactive.value && open.value && (loading.value || results.value !== null)
);
const listExpanded = computed(() => popupVisible.value && options.value.length > 0);

function optionId(index: number): string {
  return `${listboxId}-option-${index}`;
}

const activeOptionId = computed(() =>
  listExpanded.value && activeIndex.value >= 0 && activeIndex.value < options.value.length
    ? optionId(activeIndex.value)
    : undefined
);

watch(
  () => options.value.length,
  (count) => {
    if (activeIndex.value >= count) activeIndex.value = count - 1;
  }
);

watch(activeIndex, () => {
  void nextTick(() => {
    popupRef.value?.querySelector('.is-active')?.scrollIntoView?.({ block: 'nearest' });
  });
});

function cancelSearch() {
  if (searchTimer !== undefined) clearTimeout(searchTimer);
  searchTimer = undefined;
  searchAbort?.abort();
  searchAbort = null;
  searchSeq += 1;
}

function deliver(q: string, hits: readonly NxPrincipal[]) {
  results.value = hits;
  resultsQuery = q;
  loading.value = false;
  activeIndex.value = options.value.length > 0 ? 0 : -1;
  if (pickOnArrival) {
    pickOnArrival = false;
    const first = options.value[0];
    if (first) {
      pick(first);
      return;
    }
  }
  if (options.value.length === 0) announce(props.noResultsLabel);
}

function runSearch(q: string) {
  const token = ++searchSeq;
  const controller = new AbortController();
  searchAbort = controller;
  const settle = (hits: readonly NxPrincipal[] | null | undefined, error?: unknown) => {
    // A newer query (or leaving the field) has taken over: drop the answer.
    if (token !== searchSeq || controller.signal.aborted) return;
    searchAbort = null;
    if (error !== undefined) console.warn('[NxPrincipalPicker] search failed', error);
    deliver(q, Array.isArray(hits) ? hits : []);
  };
  if (!props.search) {
    settle([]);
    return;
  }
  try {
    const answer = props.search(q, controller.signal);
    if (isThenable<readonly NxPrincipal[]>(answer)) {
      answer.then(
        (hits) => settle(hits),
        (error: unknown) => settle([], error ?? 'rejected')
      );
    } else {
      settle(answer);
    }
  } catch (error) {
    settle([], error);
  }
}

function scheduleSearch() {
  cancelSearch();
  pickOnArrival = false;
  activeIndex.value = -1;
  const q = query.value.trim();
  if (q.length < props.minQueryLength) {
    results.value = null;
    resultsQuery = '';
    loading.value = false;
    open.value = false;
    return;
  }
  open.value = true;
  loading.value = true;
  if (props.debounce > 0) {
    searchTimer = setTimeout(() => {
      searchTimer = undefined;
      runSearch(q);
    }, props.debounce);
  } else {
    runSearch(q);
  }
}

/** Enter while the hits for the typed text are still to come. */
function pickFirstOnArrival() {
  pickOnArrival = true;
  if (searchTimer === undefined) return; // already on its way
  clearTimeout(searchTimer);
  searchTimer = undefined;
  runSearch(query.value.trim());
}

function openSuggestions() {
  const q = query.value.trim();
  if (!interactive.value || q.length < props.minQueryLength) return;
  if (results.value && resultsQuery === q) {
    open.value = true;
    activeIndex.value = options.value.length > 0 ? 0 : -1;
    return;
  }
  scheduleSearch();
}

function closePopup() {
  open.value = false;
  activeIndex.value = -1;
  pickOnArrival = false;
  if (loading.value) {
    cancelSearch();
    loading.value = false;
  }
}

/** Back to an empty input: the typed text is a search, never a value. */
function resetQuery() {
  cancelSearch();
  query.value = '';
  results.value = null;
  resultsQuery = '';
  loading.value = false;
  open.value = false;
  activeIndex.value = -1;
  pickOnArrival = false;
}

function asideOf(principal: NxPrincipal): string {
  if (principal.kind !== 'person' && typeof principal.memberCount === 'number') {
    return props.countLabel(principal);
  }
  return principal.sublabel ?? '';
}

// ── Changing the list ───────────────────────────────────────────────────────
function focusInput() {
  inputRef.value?.focus();
}

function pick(principal: NxPrincipal) {
  if (!interactive.value) return;
  const next = addPrincipal(model.value, principal);
  resetQuery();
  if (next !== model.value) {
    model.value = next;
    emit('add', principal);
    const away = isAbsent(principal) ? ` – ${absenceOf(principal)}` : '';
    announce(`${props.addedLabel(chipLabel(principal))}${away}`);
  }
  focusInput();
}

/** After a × the removed chip's button is gone: continue in the input, else on a neighbour. */
function restoreFocus(index: number) {
  void nextTick(() => {
    const input = inputRef.value;
    if (input && !input.disabled) {
      input.focus();
      return;
    }
    const items = rootRef.value?.querySelectorAll<HTMLElement>('.nx-principal-picker-item');
    const neighbour = items?.length ? items[Math.min(index, items.length - 1)] : undefined;
    const target =
      neighbour?.querySelector<HTMLElement>('button:not(:disabled)') ??
      rootRef.value?.querySelector<HTMLElement>('.nx-principal-picker-expand:not(:disabled)');
    target?.focus();
  });
}

function removeAt(index: number, refocus: boolean) {
  if (!interactive.value) return;
  const principal = model.value[index];
  if (!principal) return;
  model.value = model.value.filter((_, i) => i !== index);
  emit('remove', principal);
  announce(props.removedLabel(chipLabel(principal)));
  if (refocus) restoreFocus(index);
}

function replaceWithSubstitute(principal: NxPrincipal) {
  const substitute = principal.absence?.substitute;
  if (!interactive.value || !substitute) return;
  const before = model.value;
  const next = substitutePrincipal(before, principal);
  if (next === before) return;
  const added = !before.some((entry) => principalKey(entry) === principalKey(substitute));
  model.value = next;
  emit('remove', principal);
  if (added) emit('add', substitute);
  announce(
    [props.removedLabel(chipLabel(principal)), added ? props.addedLabel(chipLabel(substitute)) : '']
      .filter(Boolean)
      .join(', ')
  );
}

// ── Events ──────────────────────────────────────────────────────────────────
function onInput(event: Event) {
  query.value = (event.target as HTMLInputElement).value;
  scheduleSearch();
}

function moveActive(step: 1 | -1) {
  const count = options.value.length;
  if (count === 0) return;
  const current = activeIndex.value;
  activeIndex.value = current < 0 ? (step > 0 ? 0 : count - 1) : (current + step + count) % count;
}

function onKeydown(event: KeyboardEvent) {
  if (event.isComposing || event.keyCode === 229) return;
  switch (event.key) {
    case 'ArrowDown':
    case 'ArrowUp':
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      event.preventDefault();
      if (popupVisible.value) moveActive(event.key === 'ArrowDown' ? 1 : -1);
      else openSuggestions();
      return;
    case 'Enter': {
      if (!popupVisible.value) return;
      event.preventDefault();
      const option = options.value[activeIndex.value];
      if (option) pick(option);
      else if (loading.value) pickFirstOnArrival();
      return;
    }
    case 'Escape':
      // First the list, then the query; with neither, Escape belongs to the dialog.
      if (popupVisible.value) closePopup();
      else if (query.value) resetQuery();
      else return;
      event.preventDefault();
      event.stopPropagation();
      return;
    case 'Backspace':
      if (query.value === '' && model.value.length > 0) {
        event.preventDefault();
        removeAt(model.value.length - 1, false);
      }
      return;
  }
}

/** Focus left the whole picker: validate from now on and drop the typed text. */
function onFocusout(event: FocusEvent) {
  const next = event.relatedTarget;
  if (next instanceof Node && rootRef.value?.contains(next)) return;
  touched.value = true;
  resetQuery();
}

watch(
  () => props.readonly || props.disabled || props.compact,
  (off) => {
    if (off) resetQuery();
  }
);

onBeforeUnmount(cancelSearch);

const inputPlaceholder = computed(() => {
  if (model.value.length > 0) return props.placeholderMore;
  return (
    props.placeholder ??
    (props.variant === 'cc' ? 'optional – nur mitlesen' : 'Nutzer, Abteilung oder Gruppe …')
  );
});

// ── Absence below the field ─────────────────────────────────────────────────
const absenceNotices = computed(() =>
  absent.value.map((principal) => {
    const substitute = principal.absence?.substitute ?? null;
    return {
      principal,
      // The separator travels with the text: prettier (whitespace-insensitive
      // here) may break the line after <strong>, and Vue drops that break.
      text: ` – ${absenceOf(principal)}`,
      substitute,
      substituteText: substitute ? `→ ${principalShortLabel(substitute)}` : '',
    };
  })
);

/** The chip's absence for screen readers, after its name. */
function spokenAbsence(principal: NxPrincipal): string {
  return `, ${absenceOf(principal)}`;
}

const showAbsence = computed(
  () =>
    absent.value.length > 0 && (!!slots.absence || (props.variant === 'to' && interactive.value))
);

/** Focus the input — or, without one, the first control of the picker. */
function focus() {
  if (inputRef.value) {
    inputRef.value.focus();
    return;
  }
  rootRef.value?.querySelector<HTMLElement>('button:not(:disabled)')?.focus();
}

/** Show the required message now (e.g. on submit) and tell whether the field is valid. */
function validate(): boolean {
  touched.value = true;
  return !missing.value && !props.invalid;
}

defineExpose({ focus, validate, hasCollective, absent });
</script>

<template>
  <div
    ref="rootRef"
    class="nx-principal-picker"
    :class="{
      'is-cc': variant === 'cc',
      'is-compact': compact,
      'is-readonly': readonly,
      'is-disabled': disabled,
      'is-invalid': isInvalid,
    }"
    @focusout="onFocusout"
  >
    <NxDropdown :is-open="popupVisible" @close="closePopup">
      <template #trigger>
        <component :is="field.is" v-bind="field.attrs" class="nx-principal-picker-field">
          <ul v-if="model.length > 0" class="nx-principal-picker-chips" role="list">
            <li
              v-for="(principal, index) in model"
              :key="principalKey(principal)"
              class="nx-principal-picker-item"
            >
              <NxConditionalWrapper
                :wrap="isAbsent(principal)"
                :wrapper="NxTooltip"
                :wrapper-props="{ text: absenceOf(principal) }"
              >
                <NxChip
                  class="nx-principal-picker-chip"
                  :class="{ 'is-absent': isAbsent(principal) }"
                  size="xs"
                  :hue="chipHue(principal)"
                  :static="readonly"
                  :removable="!readonly"
                  :disabled="disabled"
                  :remove-label="removeLabel(chipLabel(principal))"
                  :data-kind="principal.kind"
                  @remove="removeAt(index, true)"
                >
                  {{ chipLabel(principal) }}
                  <template v-if="isAbsent(principal)">
                    <Plane class="nxt-icon-xs nx-principal-picker-plane" aria-hidden="true" />
                    <span class="nx-principal-picker-sr">{{ spokenAbsence(principal) }}</span>
                  </template>
                </NxChip>
              </NxConditionalWrapper>
            </li>
          </ul>
          <input
            v-if="hasInput"
            :id="inputId"
            ref="inputRef"
            class="nx-principal-picker-input"
            type="text"
            role="combobox"
            autocomplete="off"
            aria-autocomplete="list"
            :aria-expanded="listExpanded"
            :aria-controls="listExpanded ? listboxId : undefined"
            :aria-activedescendant="activeOptionId"
            :aria-label="ariaLabel"
            :aria-required="required || undefined"
            :aria-invalid="isInvalid || undefined"
            :aria-describedby="showRequiredError ? errorId : undefined"
            :placeholder="inputPlaceholder"
            :disabled="disabled"
            :value="query"
            @input="onInput"
            @keydown="onKeydown"
            @blur="closePopup"
          />
          <span v-else-if="model.length === 0" class="nx-principal-picker-empty">
            {{ emptyText }}
          </span>
          <NxIconButton
            v-if="compact && !readonly"
            class="nx-principal-picker-expand"
            variant="dashed"
            height="xs"
            :title="expandLabel"
            :disabled="disabled"
            @click="emit('expand')"
          >
            <AddIcon filled class="nxt-icon-sm" />
          </NxIconButton>
        </component>
      </template>

      <!-- mousedown.prevent: picking must not move the focus out of the input. -->
      <div v-if="hasInput" ref="popupRef" class="nx-principal-picker-popup" @mousedown.prevent>
        <div
          v-if="options.length > 0"
          :id="listboxId"
          role="listbox"
          class="nx-principal-picker-listbox"
          :class="{ 'is-stale': loading }"
          :aria-label="ariaLabel"
          :aria-busy="loading || undefined"
        >
          <div
            v-for="group in indexedGroups"
            :key="group.kind"
            role="group"
            class="nx-principal-picker-group"
            :aria-labelledby="`${listboxId}-${group.kind}`"
          >
            <div
              :id="`${listboxId}-${group.kind}`"
              role="presentation"
              class="nx-principal-picker-category"
            >
              {{ categoryLabels[group.kind] }}
            </div>
            <div
              v-for="{ principal, index } in group.items"
              :id="optionId(index)"
              :key="principalKey(principal)"
              role="option"
              class="nx-principal-picker-option"
              :class="{ 'is-active': index === activeIndex }"
              :aria-selected="index === activeIndex"
              @pointerenter="activeIndex = index"
              @click="pick(principal)"
            >
              <NxAvatar
                v-if="principal.kind === 'person'"
                :name="principal.label"
                :size="24"
                aria-hidden="true"
              />
              <span
                v-else
                class="nx-principal-picker-kind-icon"
                :style="hueVars(NX_PRINCIPAL_HUES[principal.kind])"
                aria-hidden="true"
              >
                <PeopleIcon filled class="nxt-icon-sm" />
              </span>
              <span class="nx-principal-picker-option-label">{{ principal.label }}</span>
              <NxBadge v-if="isAbsent(principal)" variant="warning" size="xs">
                {{ absenceOf(principal) }}
              </NxBadge>
              <span v-if="asideOf(principal)" class="nx-principal-picker-option-aside">
                {{ asideOf(principal) }}
              </span>
            </div>
          </div>
        </div>
        <div v-else class="nx-principal-picker-message">
          {{ loading ? searchingLabel : noResultsLabel }}
        </div>
      </div>
    </NxDropdown>

    <p v-if="showRequiredError" :id="errorId" class="nx-principal-picker-error" role="alert">
      {{ requiredMessage }}
    </p>

    <div v-if="showAbsence" class="nx-principal-picker-absence">
      <slot name="absence" :absent="absent" :replace-with-substitute="replaceWithSubstitute">
        <p
          v-for="notice in absenceNotices"
          :key="principalKey(notice.principal)"
          class="nx-principal-picker-absence-line"
        >
          <Plane class="nxt-icon-sm nx-principal-picker-plane" aria-hidden="true" />
          <span class="nx-principal-picker-absence-text">
            <strong>{{ notice.principal.label }}</strong>
            <span>{{ notice.text }}</span>
          </span>
          <NxButton
            v-if="notice.substitute"
            class="nx-principal-picker-substitute"
            variant="outline"
            height="xs"
            :title="substituteLabel(notice.substitute)"
            :aria-label="substituteLabel(notice.substitute)"
            @click="replaceWithSubstitute(notice.principal)"
          >
            {{ notice.substituteText }}
            <Plane
              v-if="notice.substitute.absence"
              class="nxt-icon-xs nx-principal-picker-plane"
              aria-hidden="true"
            />
          </NxButton>
        </p>
      </slot>
    </div>

    <span class="nx-principal-picker-live" aria-live="polite">{{ announcement }}</span>
  </div>
</template>

<style scoped>
.nx-principal-picker {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

/* The chips flow inline with the input: the list itself draws no box, its
   items are flex items of the field (list semantics stay via role="list"). */
.nx-principal-picker-chips {
  display: contents;
  list-style: none;
  margin: 0;
  padding: 0;
  /* The global type reset sizes every bare <ul>/<p> at 16px medium. */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
}
.nx-principal-picker-item {
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
}

/* Absent person: the chip's own tokens re-pointed at the warning role. */
.nx-principal-picker-chip.is-absent {
  --nxt-chip-bg: var(--nxt-warning-subtle-bg);
  --nxt-chip-bg-hover: var(--nxt-warning-subtle-bg-hover);
  --nxt-chip-border: var(--nxt-warning-border);
  --nxt-chip-fg: var(--nxt-warning-subtle-fg);
}
.nx-principal-picker-plane {
  flex: none;
  color: var(--nxt-warning-bg);
}

.nx-principal-picker-input {
  flex: 1 1 calc(var(--nxt-size-unit) * 8);
  min-width: calc(var(--nxt-size-unit) * 5);
  padding: 0 var(--nxt-space-2xs);
  border: none;
  /* The field around it draws the focus ring (NxTagField :has(input:focus-visible)). */
  outline: none;
  background: transparent;
  font: inherit;
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-input-fg);
}
.nx-principal-picker-input::placeholder {
  color: var(--nxt-input-placeholder);
}
.nx-principal-picker-input:disabled {
  cursor: not-allowed;
  color: var(--nxt-input-fg-disabled);
}

.nx-principal-picker-empty {
  padding-inline: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
}

/* Field states on NxTagField's chrome. */
.nx-principal-picker.is-invalid .nx-principal-picker-field {
  border-color: var(--nxt-input-border-invalid);
}
.nx-principal-picker.is-invalid .nx-principal-picker-field:has(input:focus-visible) {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring-invalid);
}
.nx-principal-picker.is-disabled .nx-principal-picker-field {
  background: var(--nxt-input-bg-disabled);
  cursor: not-allowed;
}
.nx-principal-picker.is-disabled .nx-principal-picker-field:hover,
.nx-principal-picker.is-readonly .nx-principal-picker-field:hover {
  border-color: var(--nxt-input-border);
}
.nx-principal-picker.is-readonly .nx-principal-picker-field {
  background: transparent;
}

/* Compact: a plain row, no field chrome. */
.nx-principal-picker.is-compact .nx-principal-picker-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

/* Suggestions: directly under the field, about seven rows, then it scrolls. */
.nx-principal-picker-popup {
  max-height: calc(var(--nxt-size-unit) * 10.5);
  overflow-y: auto;
  padding: var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-popover-bg);
  box-shadow: var(--nxt-popover-shadow);
  color: var(--nxt-popover-fg);
  font-size: var(--nxt-font-size-sm);
  user-select: none;
}
.nx-principal-picker-listbox.is-stale {
  opacity: 0.6;
}
.nx-principal-picker-category {
  padding: var(--nxt-space-xs) var(--nxt-space-sm) var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--nxt-fg-muted);
}
.nx-principal-picker-option {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-sm);
  color: var(--nxt-menu-item-fg);
  cursor: pointer;
}
.nx-principal-picker-option.is-active {
  background: var(--nxt-menu-item-bg-active);
}
.nx-principal-picker-option > * {
  flex: none;
}
.nx-principal-picker-option-label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: var(--nxt-font-weight-medium);
}
.nx-principal-picker-option-aside {
  margin-inline-start: auto;
  padding-inline-start: var(--nxt-space-xs);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  white-space: nowrap;
}
.nx-principal-picker-kind-icon {
  display: inline-grid;
  place-items: center;
  width: var(--nxt-avatar-size-sm);
  height: var(--nxt-avatar-size-sm);
  border-radius: var(--nxt-radius-full);
  background: var(--hue-subtle-bg);
  color: var(--hue-fg);
}
.nx-principal-picker-message {
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  color: var(--nxt-fg-subtle);
}

.nx-principal-picker-error {
  margin: 0;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-danger-bg);
}

.nx-principal-picker-absence {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}
.nx-principal-picker-absence-line {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin: 0;
  /* The global type reset sizes every bare <ul>/<p> at 16px medium. */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-warning-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-warning-subtle-bg);
  color: var(--nxt-warning-subtle-fg);
  font-size: var(--nxt-font-size-xs);
}
.nx-principal-picker-absence-text {
  min-width: 0;
}
.nx-principal-picker-substitute {
  margin-inline-start: auto;
  flex: none;
}

/* Visually hidden, still read. */
.nx-principal-picker-sr,
.nx-principal-picker-live {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
